import React, { useState, useEffect, useRef, useContext, createContext, useMemo } from 'react';
import { View, Text, TextInput, Pressable, Icon3 } from '../lib/react-native-compat';
import { fNum, pNum, updateUserDataArray } from '../db/company';
import { setPendingFocusField } from './EtcPopover';
import { MATERIALS_RESOURCES, SERVICES_RESOURCES } from '../helpers/resourceCodes';
import { rowTapUrl } from './tapInvoiceViewer';
import { useReversedRow, ReversedTag } from './reversedVoucher';
import { usePoSplitRow, PoSplitTag, poSplitTagWidth } from './poSplitTag';
import moment from 'moment';

// Canonical resource code → description fallback for the Resource pivot level.
// Project rows (Purchasing_Reporting_Code_*_Description) and the admin-uploaded
// Resource Code table take priority; this fills in standard codes that the
// project data / DB table didn't carry a description for.
const RESOURCE_REFERENCE = { ...MATERIALS_RESOURCES, ...SERVICES_RESOURCES };

/**
 * Shared context for third-party tab Row/RowCollapsable components.
 * Each tab (Materials, Subcontractors, Rentals, Other, AllThirdParty)
 * wraps its tree in a <ThirdPartyRowContext.Provider value={...}>.
 */
export const ThirdPartyRowContext = createContext(null);

// ── helpers ────────────────────────────────────────────────────────────

// Render formatted-zero strings ("0.00", "(0.00)", "$0", etc.) as a muted em-dash
// so empty cells visually recede instead of competing with real numbers.
const ZERO_RE = /^[\s$,()\-]*0+(?:\.0+)?[\s$,()\-]*$/;
const isZeroDisplay = v => v != null && v !== '' && ZERO_RE.test(String(v));
const NumCell = ({ style, val }) => {
    const zero = isZeroDisplay(val);
    return <Text style={[style, zero && { color: '#cbd5e1' }]}>{zero ? '—' : val}</Text>;
};

const names = {
    'Cost_Type_Code': 'Cost Type',
    'Discipline': 'Discipline',
    'Resource_Code': 'Resource',
    'Phase_Code': 'Phase',
    'Supplier_Name': 'Vendor',
    'PO_Number': 'PO',
    'PO_Detail': '',
    'Line_ID': 'Line',
    '': ''
};

const vLvl = (d, path) => {
    const pathMap = {};
    path.forEach((col, i) => { pathMap[col] = i; });
    const detail = d?.detail;
    const data = {};
    let level = 0;
    if (detail?.Forecast_Row_ID) {
        level = detail?.Forecast_Row_ID?.split('^').length - 1;
    }
    if (d?.path) {
        level = pathMap[d?.path] !== undefined ? pathMap[d?.path] : 0;
        data[d?.path] = d?.key;
    }
    if (level > 0) {
        for (let i = 0; i < level; i++) {
            data[path[i]] = detail[path[i]] ? detail[path[i]] : '';
        }
    }
    return data;
};

// ── Row ────────────────────────────────────────────────────────────────

export const ThirdPartyRow = (props) => {
    const ctx = useContext(ThirdPartyRowContext);
    if (!ctx) {
        // Rendered outside a ThirdPartyRowContext.Provider. Don't crash the tree —
        // log so we can find the offending caller via the props payload.
        if (typeof console !== 'undefined') console.warn('ThirdPartyRow: missing ThirdPartyRowContext.Provider', props);
        return null;
    }
    const {
        tabConfig, s, path, drillColumns,
        expandedBreadcrumbs, objectMapPrev,
        weekTotalMaps, varianceWeeks, weekDisplayMode,
        selectedWIP, descriptionVisible,
        phaseDayZero, phaseCCDayZero, phaseDescriptions, titleDescriptions, resourceDescriptions,
        selectedJob, selectedVersion, lockedVersion,
        userData, setUserData, focusedId, indentColors,
        setCollapseNumber, setPopoverBreadcrumb,
        poStatus, openTapDoc,
    } = ctx;

    const r = props.data?.data;

    // Force indent=1 for t^ rows (all tabs except other)
    if (tabConfig.hasTCaretIndent && r && !r.indent && r.detail?.Forecast_Row_ID?.startsWith('t^')) {
        r.indent = 1;
    }

    const marginLeft = props.newRow || r?.path == 'PO_Detail'
        ? r?.indent * 20 + (tabConfig.marginLeftOffset || 0)
        : r?.indent * 20;

    // ── compute title ──
    let title = '';
    switch (props?.data?.data?.path) {
        case 'Cost_Type_Code':
            title = tabConfig.hasTCaretIndent && r?.detail?.Forecast_Row_ID?.startsWith('t^')
                ? r?.title
                : props.newRow ? '' : titleDescriptions[r?.key] ? r?.key + ' - ' + titleDescriptions[r?.key] : r?.key;
            break;
        case 'Discipline':
            title = props.newRow ? '' : r?.key || 'Other';
            break;
        case 'Resource_Code': {
            const resDesc = resourceDescriptions?.[r?.key] || RESOURCE_REFERENCE[r?.key];
            title = props.newRow ? '' : resDesc ? r?.key + ' - ' + resDesc : r?.key || 'Unknown Resource';
            break;
        }
        case 'Phase_Code':
            title = tabConfig.hasTCaretIndent && r?.detail?.Forecast_Row_ID?.startsWith('t^')
                ? r?.title
                : props.newRow ? '' : phaseDescriptions[r?.key] ? r?.key + ' - ' + phaseDescriptions[r?.key] : r?.key || 'Unknown ' + names[r?.path];
            break;
        case 'Supplier_Name':
            title = tabConfig.hasTCaretIndent && r?.detail?.Forecast_Row_ID?.startsWith('t^')
                ? r?.title
                : props.newRow ? '' : r?.key ? r?.key : 'Unknown ' + names[r?.path];
            break;
        case 'PO_Number':
            title = props.newRow ? '' : r?.key == '' ? r?.detail?.Is_Credit_Card_Transaction == 'Y' ? 'Credit Card' : 'Unknown ' + names[r?.path] : r?.key;
            break;
        case 'PO_Detail':
            if (tabConfig.poDetailTitlePriority === 'remark') {
                title = props.newRow ? 'new' : r?.detail?.Remark_Description ? r?.detail?.Remark_Description : r?.detail?.PO_Line_Description ? r?.detail?.PO_Line_Description : '';
            } else {
                title = props.newRow ? 'new' : r?.detail?.PO_Line_Description ? r?.detail?.PO_Line_Description : r?.detail?.Remark_Description ? r?.detail?.Remark_Description : '';
            }
            break;
        case 'Line_ID':
            let desc;
            if (tabConfig.lineIdFallback === 'transaction') {
                desc = r?.detail?.Transaction_Description ? r?.detail?.Transaction_Description : r?.detail?.PO_Line_Description ? r?.detail?.PO_Line_Description : '';
            } else {
                desc = r?.detail?.CH ? 'Unknown PO Detail' : r?.detail?.PO_Line_Description ? r?.detail?.PO_Line_Description : r?.detail?.Remark_Description ? r?.detail?.Remark_Description : '';
            }
            title = props.newRow ? '' : r?.detail?.Line_ID ? r?.detail?.Line_ID + ' - ' + desc : desc;
            break;
    }

    // "RV": POs under this row whose voucher was posted and reversed in full,
    // which is why their cost reads blank.
    const reversedPos = useReversedRow(props.data, props.newRow);
    const rvTag = reversedPos.length
        ? <ReversedTag entries={reversedPos} node={props.data} style={{ position: 'absolute', right: 4, top: 7 }} />
        : null;
    // "4/6 lines · 2 on Other": some of this PO's lines are coded to a cost type
    // another tab owns, so only part of the PO shows here.
    const poSplit = usePoSplitRow(props.data, tabConfig.tab, props.newRow);
    const splitTag = poSplit
        ? <PoSplitTag split={poSplit} style={{ position: 'absolute', right: rvTag ? 30 : 4, top: 0, bottom: 0 }} />
        : null;
    // Always set (2 = the cell's own padding) so React never drops the longhand
    // out from under the `padding` shorthand when a row gains or loses its tag.
    const rvTitlePad = { paddingRight: (!rvTag ? 2 : reversedPos.length > 1 && r?.path !== 'PO_Number' && r?.path !== 'Line_ID' ? 40 : 28) + poSplitTagWidth(poSplit) };

    const isIcon = props.isIcon;
    const po = r?.path === drillColumns[drillColumns.length - 1];
    const topLevels = r?.path !== 'PO_Detail' && r?.path !== 'Line_ID' && r?.path !== '';
    const backgroundColor = indentColors[r?.indent];
    const atEndUser = r?.detail?.Forecast_Row_ID && path[r?.detail?.Forecast_Row_ID.split('^').length - 2] == r?.path;
    const userRow = props.newRow || props.userAdded || atEndUser ? true : false;
    const userSaved = r?.detail?.Forecast_Row_ID ? true : false;
    let rowData = props.newRow ? {} : r?.detail;

    // ── state ──
    const [noteHeight, setNoteHeight] = useState(0);
    const [description, setDescription] = useState(
        r?.detail?.Description ? r?.detail?.Description
        : r?.detail?.description ? r?.detail?.description
        : r?.description && !props.newRow ? r?.description
        : r?.path == 'PO_Detail' || r?.path == 'Line_ID'
            ? r?.detail?.PO_Line_Description_2 ? r?.detail?.PO_Line_Description_2
            : r?.detail?.Remark_Description ? r?.detail?.Remark_Description : '' : ''
    );
    const [notes, setNotes] = useState(
        !props.newRow || userSaved
            ? props?.data?.data?.notes ? props?.data?.data?.notes
            : props.data?.data?.detail?.notes ? props.data?.data?.detail?.notes : '' : ''
    );
    const [varianceBudget, setVarianceBudget] = useState(!props.newRow ? r?.varianceBudget ? fNum(r?.varianceBudget) : '' : '');
    const computeEtcDisplay = (d) => {
        if (!d) return '';
        if (d.etcTotal != null && `${d.etcTotal}` !== '') return fNum(pNum(d.etcTotal));
        if (d.etc != null && `${d.etc}` !== '') return fNum(pNum(d.etc));
        return '';
    };
    const [etc, setEtc] = useState(!props.newRow ? computeEtcDisplay(r) : '');
    const currentBudget = useMemo(() =>
        tabConfig.budgetInit
            ? tabConfig.budgetInit(r, phaseDayZero, phaseCCDayZero)
            : r?.path == 'Phase_Code' && phaseDayZero ? phaseDayZero[r.key] ? fNum(phaseDayZero[r.key]) : '' : ''
    , [tabConfig.budgetInit, r?.path, r?.key, phaseDayZero, phaseCCDayZero]);
    const [total, setTotal] = useState(!props.newRow ? r?.totalTotal ? fNum(r?.totalTotal) : '' : '');
    const [rowId] = useState(Math.random().toString(16).substring(1));
    const [open, setOpen] = useState(!props.newRow ? r?.openTotal ? fNum(r?.openTotal) : '' : '');
    const [actualCosts, setActualCosts] = useState(!props.newRow ? r?.actualTotal ? fNum(r?.actualTotal) : '' : '');
    const [maxGLDate, setMaxGLDate] = useState(r?.maxGLDate ? moment(r?.maxGLDate).format('M/D/YYYY') : '');
    const [userTitle, setUserTitle] = useState(
        tabConfig.editableTitle
            ? (r?.detail?.Row_Label ? r?.detail?.Row_Label : title)
            : ''
    );
    const inputsRef = useRef([]);
    const pendingUpdateRef = useRef(null);
    // Tracks the most recent debounced save args so a beforeunload / unmount
    // flush can fire the save synchronously instead of losing the edit.
    const pendingSaveRef = useRef(null);
    const textTimeoutRef = useRef(null);
    const [localFocusedId, setLocalFocusedId] = useState(focusedId.current);
    const uniqueId = rowData?.Forecast_Row_ID ? rowData?.Forecast_Row_ID : props.newRow ? r?.breadcrumb ? r.breadcrumb + '^~u' + rowId : r?.key : r?.breadcrumb ? r.breadcrumb : r?.key;
    const [selectedCostType, setSelectedCostType] = useState(
        tabConfig.showCostTypeDropdown
            ? (!props.newRow || userSaved
                ? props?.data?.data?.costType ? props?.data?.data?.costType
                : props.data?.data?.detail?.costType ? props.data?.data?.detail?.costType
                : props.data?.data?.detail?.Cost_Type_Code ? props.data?.data?.detail?.Cost_Type_Code + ''
                : tabConfig.defaultCostType : '')
            : ''
    );

    // Percent (materials / allThirdParty detail columns)
    const percent = !isNaN(rowData?.Ordered_Quantity) && rowData?.Ordered_Quantity > 0 ? rowData?.Ordered_Quantity_Received / rowData?.Ordered_Quantity * 100 + '%' : '';
    const percentCleaned = percent && percent != 'NaN%' ? percent : '';

    // JDE link (other.js)
    const jdeLink = props?.data?.data?.path == 'PO_Detail' && r?.detail?.Media_Object_URL ? r?.detail?.Media_Object_URL : '';

    // Scanned vendor-invoice link for a PO-line leaf row (shared with Billing).
    // Prefers the row's own Media_Object_URL, else the PO-match rollup's TAP_URL;
    // opens the PDF in the shared inline viewer. Rendered as the last detail
    // column, so it only shows when the detail columns are expanded.
    const leafTapUrl = (r?.path === 'PO_Detail' || r?.path === 'Line_ID')
        ? rowTapUrl(r?.detail, poStatus) : '';
    const invoiceCell = (
        <View style={{ width: 100, height: 28, alignItems: 'center', justifyContent: 'center' }}>
            {leafTapUrl && openTapDoc ? (
                <Pressable onPress={() => openTapDoc(leafTapUrl, [
                    r?.detail?.Supplier_Name,
                    r?.detail?.PO_Number && `PO ${r.detail.PO_Number}`,
                    r?.detail?.Line_ID != null && r?.detail?.Line_ID !== '' ? `Line ${r.detail.Line_ID}` : '',
                ].filter(Boolean).join(' · '))}>
                    <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600', textDecorationLine: 'underline' }}>View</Text>
                </Pressable>
            ) : null}
        </View>
    );

    // ── effects ──

    useEffect(() => {
        if (tabConfig.editableTitle && focusedId.current == (uniqueId + '-title')) {
            inputsRef.current[uniqueId + '-title']?.focus();
        } else if (focusedId.current == (uniqueId + '-description')) {
            inputsRef.current[uniqueId + '-description']?.focus();
        } else if (focusedId.current == (uniqueId + '-notes')) {
            inputsRef.current[uniqueId + '-notes']?.focus();
        } else if (focusedId.current == (uniqueId + '-etc')) {
            inputsRef.current[uniqueId + '-etc']?.focus();
        }
    }, [localFocusedId]);

    useEffect(() => {
        setTotal(fNum(pNum(actualCosts) + pNum(open) + pNum(etc)));
        setVarianceBudget(currentBudget ? fNum(pNum(currentBudget) - (pNum(actualCosts) + pNum(open) + pNum(etc))) : '');
    }, [etc, currentBudget]);

    // Sync ETC display from recalculated tree data (rollup: own ETC + children ETC)
    useEffect(() => {
        if (props.newRow) return;
        const newR = props.data?.data;
        if (!newR) return;
        const newEtcDisplay = computeEtcDisplay(newR);
        // Only sync when not actively editing this field
        if (focusedId.current !== uniqueId + '-etc') {
            setEtc(newEtcDisplay);
        }
        // Sync actual costs, open commitments, date, and totals from tree data
        const newOpen = newR?.openTotal ? fNum(newR.openTotal) : '';
        const newActual = newR?.actualTotal ? fNum(newR.actualTotal) : '';
        const newDate = newR?.maxGLDate ? moment(newR.maxGLDate).format('M/D/YYYY') : '';
        setOpen(newOpen);
        setActualCosts(newActual);
        setMaxGLDate(newDate);
        const newTotal = newR?.totalTotal ? fNum(newR.totalTotal) : fNum(pNum(newActual) + pNum(newOpen) + pNum(newEtcDisplay));
        setTotal(newTotal);
        setVarianceBudget(currentBudget ? fNum(pNum(currentBudget) - (pNum(newActual) + pNum(newOpen) + pNum(newEtcDisplay))) : '');
    }, [props.data]);

    // ── save ──

    const saveObject = async (fieldName, value, newRow = null) => {
        setCollapseNumber(0);
        const d = props?.data?.data;
        const levelData = vLvl(d, path);

        // Compute costType for the save payload
        let costType;
        if (tabConfig.costTypeFromData) {
            costType = levelData?.Cost_Type_Code ? levelData?.Cost_Type_Code + '' : d?.detail?.Cost_Type_Code ? d?.detail?.Cost_Type_Code + '' : '';
        } else if (tabConfig.fixedCostType) {
            costType = tabConfig.fixedCostType;
        } else {
            costType = fieldName == 'costType' ? value : selectedCostType ? selectedCostType : tabConfig.defaultCostType;
        }

        // Compute drill-level label to persist in Equipment_Tier column
        const levelLabel = d?.path ? (names[d.path] || d.path) : '';

        // Resolve a known GUID for this cell: the tree's detail first, then any
        // existing userData row at the same Row_ID (so a GUID learned from a
        // prior insert this session is used even before the tree rebuilds).
        const existingUserRow = userData.find(
            item => item.Forecast_Row_ID === uniqueId && item.Forecast_Tab === tabConfig.tab && item.Forecast_User_Data_GUID
        );
        const knownGuid = (d?.detail?.Forecast_User_Data_GUID || existingUserRow?.Forecast_User_Data_GUID) || '';

        let data = {
            active: 1,
            userId: '154045',
            ...(knownGuid ? { updateRow: 1, guidId: knownGuid + '' } : {}),
            userName: 'Seth Bailey',
            fieldName: 'etc',
            tab: tabConfig.tab,
            rowId: uniqueId,
            version: selectedVersion + '',
            value: value + '',
            id: selectedJob,
            costType: costType,
            costCode: levelData?.Phase_Code ? levelData?.Phase_Code + '' : (tabConfig.costCodeFallback && d?.detail?.Phase_Code ? d?.detail?.Phase_Code + '' : ''),
            vendor: levelData?.Supplier_Name ? levelData?.Supplier_Name : '',
            po: levelData?.PO_Number ? levelData?.PO_Number?.toString().substring(0, 10) + '' : '',
            equipment: levelLabel,
            lockedVersion: lockedVersion,
            valueAmount: fieldName == 'etc' ? pNum(value) : etc ? pNum(etc) : 0,
            description: fieldName == 'description' ? value : description,
            notes: fieldName == 'notes' ? value : notes,
            ...(tabConfig.editableTitle ? { rowLabel: fieldName == 'title' ? value : userTitle } : {}),
        };

        const stateUpdate = {
            Forecast_Field_Name: 'etc',
            Forecast_Value: value,
            Forecast_Row_ID: data.rowId,
            Forecast_Tab: tabConfig.tab,
            Forecast_User_Data_GUID: knownGuid || '',
            Phase_Code: levelData?.Phase_Code ? levelData?.Phase_Code : (tabConfig.costCodeFallback && d?.detail?.Phase_Code ? d?.detail?.Phase_Code : ''),
            Supplier_Name: levelData?.Supplier_Name ? levelData?.Supplier_Name : '',
            PO_Number: levelData?.PO_Number ? levelData?.PO_Number : '',
            Equipment_Tier: levelLabel,
            BU_ID: selectedJob,
            Cost_Code: levelData?.Phase_Code ? levelData?.Phase_Code : (tabConfig.costCodeFallback && d?.detail?.Phase_Code ? d?.detail?.Phase_Code + '' : ''),
            Cost_Type: costType,
            Value_Amount: fieldName == 'etc' ? pNum(value) : etc ? pNum(etc) : 0,
            Notes: fieldName == 'notes' ? value : notes,
            Description: fieldName == 'description' ? value : description,
            ...(tabConfig.editableTitle ? { Row_Label: fieldName == 'title' ? value : userTitle } : {}),
        };

        // Optimistic update first (snappy UI), unchanged.
        if (tabConfig.deferredSave) {
            pendingUpdateRef.current = stateUpdate;
        } else {
            const filteredUData = userData.filter(
                item => !(item.Forecast_Row_ID === stateUpdate.Forecast_Row_ID && item.Forecast_Tab === tabConfig.tab)
            );
            filteredUData.push(stateUpdate);
            setUserData(filteredUData);
        }

        // Fire the save. When it returns, an INSERT (no prior GUID) hands back
        // the new Forecast_User_Data_GUID — stamp it onto our row so the next
        // edit updates by GUID instead of re-running the racy natural-key upsert.
        updateUserDataArray(data).then((guidResults) => {
            if (knownGuid) return;
            const resolvedGuid = (Array.isArray(guidResults) && guidResults[0]) || null;
            if (resolvedGuid == null) return;
            if (pendingUpdateRef.current && pendingUpdateRef.current.Forecast_Row_ID === stateUpdate.Forecast_Row_ID) {
                pendingUpdateRef.current = { ...pendingUpdateRef.current, Forecast_User_Data_GUID: resolvedGuid };
            }
            setUserData(prev => prev.map(item =>
                (item.Forecast_Row_ID === stateUpdate.Forecast_Row_ID
                    && item.Forecast_Tab === tabConfig.tab
                    && !item.Forecast_User_Data_GUID)
                    ? { ...item, Forecast_User_Data_GUID: resolvedGuid }
                    : item
            ));
        });
    };

    const flushAtomUpdate = () => {
        if (pendingUpdateRef.current) {
            const stateUpdate = pendingUpdateRef.current;
            pendingUpdateRef.current = null;
            const filteredUData = userData.filter(
                item => !(item.Forecast_Row_ID === stateUpdate.Forecast_Row_ID && item.Forecast_Tab === tabConfig.tab)
            );
            filteredUData.push(stateUpdate);
            setUserData(filteredUData);
        }
    };

    // Debounced save that also records the pending args so we can flush on
    // beforeunload / unmount if the timeout hasn't fired yet.
    const scheduleSave = (fieldName, value, newRow, focusField) => {
        clearTimeout(textTimeoutRef.current);
        pendingSaveRef.current = { fieldName, value, newRow };
        textTimeoutRef.current = setTimeout(() => {
            saveObject(fieldName, value, newRow);
            pendingSaveRef.current = null;
            if (focusField) setLocalFocusedId(uniqueId + '-' + focusField);
        }, 1000);
    };

    const flushPendingSave = () => {
        if (!pendingSaveRef.current) return;
        clearTimeout(textTimeoutRef.current);
        const { fieldName, value, newRow } = pendingSaveRef.current;
        pendingSaveRef.current = null;
        saveObject(fieldName, value, newRow);
    };

    // Flush any in-flight debounced save when the tab closes or the row
    // unmounts (version switch, drill change, etc.).
    useEffect(() => {
        const onBeforeUnload = () => { flushPendingSave(); };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            flushPendingSave();
        };
    }, []);

    // ── budget display ──
    const budgetDisplay = (() => {
        if (tabConfig.budgetDisplay) return tabConfig.budgetDisplay(props, r, currentBudget, userSaved);
        return props.newRow || userSaved ? '' : currentBudget;
    })();

    // ── variance weeks (shared) ──
    const renderVarianceWeeks = () => varianceWeeks.map(guid => {
        const weekVal = guid === selectedWIP?.Version_GUID
            ? objectMapPrev[r?.breadcrumb]
            : weekTotalMaps[guid]?.map?.[r?.breadcrumb];
        const mode = weekDisplayMode[guid] || 'var';
        let displayVal;
        if (tabConfig.stickyTitle) {
            if (mode === 'var' && !props.newRow && r?.totalTotal != null) {
                // Always show variance: if no prior week data, prior is 0
                displayVal = pNum(r?.totalTotal) - pNum(weekVal || 0);
            } else {
                displayVal = weekVal;
            }
        } else {
            if (mode === 'var' && !props.newRow && r?.totalTotal != null) {
                displayVal = fNum(pNum(r?.totalTotal) - pNum(weekVal || 0));
            } else {
                displayVal = weekVal ? fNum(weekVal) : '';
            }
        }
        return (
            <Text key={guid} style={[s().notEdit, { backgroundColor: mode === 'var' ? '#fef9ee' : '#f8faff' }]}>
                {tabConfig.stickyTitle
                    ? (!props.newRow && displayVal != null ? fNum(displayVal) : '')
                    : (!props.newRow ? displayVal : '')
                }
            </Text>
        );
    });

    // ── ETC input (shared) ──
    const ownEtc = r?.etc != null && `${r?.etc}` !== '' ? pNum(r?.etc) : 0;
    const childEtc = r?.etcTotal != null ? pNum(r?.etcTotal) - ownEtc : 0;
    const hasBreakdown = !props.newRow && ownEtc !== 0 && childEtc !== 0;
    const [showEtcBreakdown, setShowEtcBreakdown] = useState(false);
    const canOpenPopover = !!r?.breadcrumb && !!setPopoverBreadcrumb;

    // ── notes column (shared) ──
    // Click opens EtcPopover modal with the notes field focused instead of
    // inline editing (avoids duplicate-row creation on parent rows).
    const renderNotes = () => {
        if (props.newRow || !topLevels) return <Text style={s(400).notEdit}></Text>;
        if (canOpenPopover) {
            return (
                <View style={{ position: 'relative' }}>
                    <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
                    <Pressable onPress={() => { setPendingFocusField('notes'); setPopoverBreadcrumb(r.breadcrumb); }} style={{ cursor: 'pointer' }}>
                        <Text style={[s(400).notEdit, { paddingLeft: 10, minHeight: 28, textAlign: 'left', whiteSpace: 'pre-wrap' }]}>{notes || ''}</Text>
                    </Pressable>
                </View>
            );
        }
        return (
            <View style={{ position: 'relative' }}>
                <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
                <TextInput style={[po ? s(400).edit : s(400).notEdit, { paddingLeft: 10, height: Math.max(28, noteHeight) }]}
                    value={notes}
                    ref={e => { inputsRef.current[uniqueId + '-notes'] = e }}
                    multiline={true}
                    onContentSizeChange={(event) =>
                        setNoteHeight(event.nativeEvent.contentSize.height)
                    }
                    onChangeText={n => {
                        setNotes(n);
                        if (n.length > 0) scheduleSave('notes', n, userRow, 'notes');
                        else clearTimeout(textTimeoutRef.current);
                    }}
                    onFocus={(e) => {
                        focusedId.current = uniqueId + '-notes';
                        e.currentTarget.setSelectionRange(
                            e.currentTarget.value.length,
                            e.currentTarget.value.length
                        );
                    }}
                ></TextInput>
            </View>
        );
    };

    const etcNegative = !props.newRow && pNum(etc) < 0;
    const renderEtcInput = () => (
        <View style={{ position: 'relative' }}
            onMouseEnter={() => { if (hasBreakdown) setShowEtcBreakdown(true); }}
            onMouseLeave={() => setShowEtcBreakdown(false)}
        >
            <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
            {canOpenPopover ? (
                <Pressable onPress={() => setPopoverBreadcrumb(r.breadcrumb)} style={{ cursor: 'pointer' }}>
                    <Text style={[s(120).notEdit, { paddingRight: 8, color: props.newRow ? '#9ca3af' : etcNegative ? '#dc2626' : undefined }]}>{props.newRow ? '--' : etc}</Text>
                </Pressable>
            ) : (
                <TextInput style={[s(120).edit, etcNegative && { color: '#dc2626' }]}
                    ref={e => { inputsRef.current[uniqueId + '-etc'] = e }}
                    onFocus={() => { focusedId.current = uniqueId + '-etc' }}
                    value={etc} onChangeText={etc => {
                        setEtc(etc);
                        if (etc.length > 0) scheduleSave('etc', etc, userRow, 'etc');
                        else clearTimeout(textTimeoutRef.current);
                    }}
                    {...(tabConfig.deferredSave ? { onBlur: () => flushAtomUpdate() } : {})}
                ></TextInput>
            )}
            {showEtcBreakdown && (
                <View style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: 6, padding: 8, minWidth: 180, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 8, elevation: 6 }}>
                    <Text style={{ fontSize: 11, fontWeight: '700', color: '#374151', marginBottom: 4 }}>ETC Breakdown</Text>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                        <Text style={{ fontSize: 11, color: '#6b7280' }}>This level:</Text>
                        <Text style={{ fontSize: 11, fontWeight: '600', color: '#1f2937' }}>{fNum(ownEtc)}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                        <Text style={{ fontSize: 11, color: '#6b7280' }}>Below:</Text>
                        <Text style={{ fontSize: 11, fontWeight: '600', color: '#1f2937' }}>{fNum(childEtc)}</Text>
                    </View>
                    <View style={{ borderTop: '1px solid #e5e7eb', marginTop: 2, paddingTop: 2, flexDirection: 'row', justifyContent: 'space-between' }}>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#374151' }}>Total:</Text>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#374151' }}>{fNum(pNum(r?.etcTotal))}</Text>
                    </View>
                </View>
            )}
        </View>
    );

    // ── RENDER ──

    if (tabConfig.stickyTitle) {
        // Sticky layout: materials, subcontractors, rentals, allThirdParty
        // When rendered inside ThirdPartyRowCollapsable (isIcon), the indent spacer
        // lives before the chevron, so skip it here and shrink the title cell to match.
        const stickyLeft = isIcon ? 22 + marginLeft : 22;
        const titleCellWidth = isIcon ? 300 - marginLeft : 300;
        const titleSpacerWidth = isIcon ? 0 : marginLeft;
        return (
            <View style={{ flexDirection: 'row' }}>
                <View style={{ flexDirection: 'row', position: 'sticky', left: stickyLeft, zIndex: 10, backgroundColor: 'white' }}>
                    <View style={{ width: titleCellWidth, flexDirection: 'row', position: 'relative' }}>
                        <View style={{ width: titleSpacerWidth }}></View>
                        {tabConfig.editableTitle && (props.newRow || r?.detail?.Row_Label || r?.detail?.Forecast_Row_ID?.startsWith('t^')) ?
                            <View style={[{ position: 'relative', flex: 1 }]}>
                                <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
                                <TextInput style={[s().firstNotEdit, { backgroundColor: backgroundColor, textAlign: 'left', paddingLeft: 10, width: 300 - marginLeft }]}
                                    value={userTitle}
                                    ref={e => { inputsRef.current[uniqueId + '-title'] = e }}
                                    onChangeText={t => {
                                        setUserTitle(t);
                                        if (t.length > 0) scheduleSave('title', t, true, 'title');
                                        else clearTimeout(textTimeoutRef.current);
                                    }}
                                    onFocus={() => { focusedId.current = uniqueId + '-title' }}
                                ></TextInput>
                            </View>
                            : <Text numberOfLines={1} style={[s().firstNotEdit, { backgroundColor: backgroundColor, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, rvTitlePad]}>{props.newRow ? '' : title}</Text>}
                        {rvTag}
                        {splitTag}
                    </View>
                    <View style={{ position: 'relative' }}>
                        <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
                        {!props.newRow && canOpenPopover ? (
                            <Pressable onPress={() => { setPendingFocusField('description'); setPopoverBreadcrumb(r.breadcrumb); }} style={{ cursor: 'pointer' }}>
                                <Text style={[s(140).notEdit, { paddingLeft: 10, backgroundColor: backgroundColor, textAlign: 'left' }]}>{description || ''}</Text>
                            </Pressable>
                        ) : (
                            <TextInput style={[s(140).edit, { paddingLeft: 10, backgroundColor: backgroundColor }]}
                                ref={e => { inputsRef.current[uniqueId + '-description'] = e }}
                                value={description} onChangeText={n => {
                                    setDescription(n);
                                    if (n.length > 0) scheduleSave('description', n, true, 'description');
                                    else clearTimeout(textTimeoutRef.current);
                                }}
                                onFocus={() => { focusedId.current = uniqueId + '-description' }}
                            ></TextInput>
                        )}
                    </View>
                </View>
                <View style={{ flexDirection: 'row', backgroundColor: backgroundColor }}>
                    {descriptionVisible && tabConfig.renderDetailColumns ? tabConfig.renderDetailColumns(r, rowData, s, fNum, moment, percentCleaned) : null}
                    {descriptionVisible ? invoiceCell : null}
                    <View style={{ width: 10, backgroundColor: 'white' }}></View>
                    <NumCell style={s().notEdit} val={budgetDisplay} />
                    <View style={{ width: 10, backgroundColor: 'white' }}></View>
                    <Text style={s().notEdit}>{props.newRow ? '' : maxGLDate}</Text>
                    <NumCell style={s().notEdit} val={props.newRow ? '' : actualCosts} />
                    <NumCell style={s().notEdit} val={props.newRow ? '' : open} />
                    {renderEtcInput()}
                    <NumCell style={s().notEdit} val={props.newRow ? '' : total} />
                    <NumCell style={s().notEdit} val={props.newRow ? '' : varianceBudget} />
                    {renderVarianceWeeks()}
                    {tabConfig.showCostTypeDropdown ? <View style={{ width: 120 }}><select
                        style={{ width: 120, height: 28, border: '1px solid #ccc', padding: '0 6px', fontSize: 10 }}
                        value={selectedCostType}
                        onChange={(e) => {
                            setSelectedCostType(e.target.value);
                            saveObject('costType', e.target.value, userRow);
                        }}
                    >
                        <option value="">Select Cost Type</option>
                        {(() => {
                            const groups = [];
                            let currentGroup = null;
                            tabConfig.costTypeDescriptions.forEach((option) => {
                                if (option.group && option.group !== currentGroup) {
                                    currentGroup = option.group;
                                    groups.push({ group: currentGroup, options: [] });
                                }
                                if (groups.length === 0) groups.push({ group: null, options: [] });
                                groups[groups.length - 1].options.push(option);
                            });
                            return groups.map((g, gi) =>
                                g.group ? (
                                    <optgroup key={gi} label={g.group}>
                                        {g.options.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </optgroup>
                                ) : (
                                    g.options.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))
                                )
                            );
                        })()}
                    </select></View> : null}
                    {renderNotes()}
                </View>
            </View>
        );
    } else {
        // Non-sticky layout: other.js
        const budgetLevel = r?.path == 'Cost_Type_Code' || r?.path == 'Phase_Code';
        return (
            <View style={{ flexDirection: 'row' }}>
                <View style={{ position: 'relative' }}>
                    <Text numberOfLines={1} style={[s(300 - marginLeft + (isIcon ? 0 : 22)).notEdit, { marginLeft: marginLeft, backgroundColor: backgroundColor, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, rvTitlePad]}>{title}</Text>
                    {rvTag}
                    {splitTag}
                </View>
                <View style={{ flexDirection: 'row', backgroundColor: backgroundColor }}>
                    <View style={{ position: 'relative' }}>
                        <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
                        {!props.newRow && canOpenPopover ? (
                            <Pressable onPress={() => { setPendingFocusField('description'); setPopoverBreadcrumb(r.breadcrumb); }} style={{ cursor: 'pointer' }}>
                                <Text style={[s(140).notEdit, { paddingLeft: 10, textAlign: 'left' }]}>{description || ''}</Text>
                            </Pressable>
                        ) : (
                            <TextInput style={[po || props.newRow ? s(140).edit : s(140).notEdit]}
                                onFocus={() => { focusedId.current = uniqueId + '-description' }}
                                ref={e => { inputsRef.current[uniqueId + '-description'] = e }}
                                value={description} onChangeText={n => {
                                    setDescription(n);
                                    if (n.length > 0) scheduleSave('description', n, true, 'description');
                                    else clearTimeout(textTimeoutRef.current);
                                }}
                            ></TextInput>
                        )}
                    </View>
                    {descriptionVisible && tabConfig.renderDetailColumns ? tabConfig.renderDetailColumns(r, rowData, s, fNum, moment, percentCleaned) : null}
                    {descriptionVisible ? invoiceCell : null}
                    <View style={{ width: 10, backgroundColor: 'white' }}></View>
                    <NumCell style={s().notEdit} val={budgetDisplay} />
                    <View style={{ width: 10, backgroundColor: 'white' }}></View>
                    <Text style={s().notEdit}>{!props.newRow ? maxGLDate : ''}</Text>
                    <NumCell style={s().notEdit} val={actualCosts} />
                    <NumCell style={s().notEdit} val={open} />
                    {renderEtcInput()}
                    <NumCell style={s().notEdit} val={!props.newRow ? total : ''} />
                    <NumCell style={s().notEdit} val={!props.newRow ? varianceBudget : ''} />
                    {renderVarianceWeeks()}
                    {renderNotes()}
                </View>
            </View>
        );
    }
};

// ── RowCollapsable ─────────────────────────────────────────────────────

export const ThirdPartyRowCollapsable = (props) => {
    const ctx = useContext(ThirdPartyRowContext);
    if (!ctx) {
        if (typeof console !== 'undefined') console.warn('ThirdPartyRowCollapsable: missing ThirdPartyRowContext.Provider', props);
        return null;
    }
    const { tabConfig, drillColumns, expandedBreadcrumbs, setExpandedBreadcrumbs, collapseAll, collapseNumber } = ctx;

    // Build a clean "new row" object from the parent — only structural columns survive
    const buildNewP = (parentData) => {
        const parentDetail = parentData?.data?.detail || {};
        const cleanDetail = {};
        (drillColumns || []).forEach(col => {
            if (parentDetail[col] !== undefined) cleanDetail[col] = parentDetail[col];
        });
        if (parentDetail.Phase_Code) cleanDetail.Phase_Code = parentDetail.Phase_Code;
        if (parentDetail.Cost_Type_Code) cleanDetail.Cost_Type_Code = parentDetail.Cost_Type_Code;
        if (parentDetail.BU_ID) cleanDetail.BU_ID = parentDetail.BU_ID;
        return {
            ...parentData,
            data: {
                ...parentData.data,
                indent: parentData.data.indent + 1,
                description: '',
                notes: '',
                title: '',
                detail: cleanDetail,
                ...(tabConfig.newPExtraFields || {})
            }
        };
    };

    const [expanded, setExpanded] = useState(props.data?.data?.expanded ? props.data?.data?.expanded : false);
    const [p, setP] = useState(props.data);
    const [newP, setNewP] = useState(() => buildNewP(props.data));

    useEffect(() => {
        if (collapseNumber) {
            setExpanded(false);
        }
    }, [collapseAll]);

    useEffect(() => {
        setP(props.data);
        setNewP(buildNewP(props.data));
    }, [props.data]);

    const childPadding = tabConfig.collapsableChildPadding ?? 22;

    // An OC (open-commitment) PO_Detail leaf duplicates its Line_ID parent node,
    // so it's hidden when the drill ends at Line_ID. But when the drill stops at
    // PO_Number (e.g. the Other tab), that leaf is the ONLY representation of the
    // PO line — hiding it leaves the PO expanding to an empty area. Only suppress
    // OC leaves when a Line_ID level actually exists above them.
    const hideOcLeaves = drillColumns?.[drillColumns.length - 1] === 'Line_ID';

    const indentPx = (p?.data?.indent || 0) * 20;
    const stickyChevron = tabConfig.stickyTitle
        ? { position: 'sticky', left: 0, zIndex: 11, backgroundColor: 'white', flexDirection: 'row', alignItems: 'center' }
        : {};

    return (
        <View>
            <View style={{ flexDirection: 'row', cursor: 'pointer' }}>
                <View style={stickyChevron}>
                    {tabConfig.stickyTitle && indentPx > 0 && <View style={{ width: indentPx }} />}
                    {Object.values(p?.rows || {})?.length ? <Icon3
                        onPress={() => {
                            props.data.data.expanded = !expanded;
                            expandedBreadcrumbs[props.data.data.breadcrumb] = !expanded;
                            setExpandedBreadcrumbs(expandedBreadcrumbs);
                            setExpanded(!expanded);
                        }}
                        name={expanded ? "MdExpandMore" : "MdChevronRight"}
                        size={22}
                        color="#4b5563"
                        style={{ transition: 'transform 0.2s ease' }}
                    /> : <View style={{ width: 22 }}></View>}
                </View>
                <ThirdPartyRow isIcon={true} data={p} />
            </View>
            <View style={{ overflow: expanded ? 'visible' : 'hidden', transition: 'max-height 0.25s ease-in-out, opacity 0.2s ease', maxHeight: expanded ? 5000 : 0, opacity: expanded ? 1 : 0 }}>
                {p.rows && Object.values(p.rows)?.sort((a, b) => ('' + a?.key).localeCompare(b?.key)).map((row, index) => {
                    return (
                        <View key={index}>
                            {row.rows ?
                                <ThirdPartyRowCollapsable data={row} /> :
                                childPadding > 0
                                    ? <View style={{ paddingLeft: childPadding }}>{!(hideOcLeaves && row.data.detail?.OC) ? <ThirdPartyRow data={row} /> : null}</View>
                                    : <View>{!(hideOcLeaves && row.data.detail?.OC) ? <ThirdPartyRow data={row} /> : null}</View>
                            }
                        </View>
                    )
                })}
                {(() => {
                    const lastDrill = drillColumns[drillColumns.length - 1];
                    const noNewRow = ['PO_Number', 'PO_Detail', 'Line_ID'].includes(props.data.data?.path) || props.data.data?.path === lastDrill;
                    return noNewRow ? null
                        : childPadding > 0
                            ? <View style={{ paddingLeft: childPadding }}><ThirdPartyRow newRow={true} data={newP} /></View>
                            : <ThirdPartyRow newRow={true} data={newP} />;
                })()}
            </View>
        </View>
    );
};
